import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Breadcrumbs, Typography } from '@mui/material'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  ApprovalCommentDialog,
  MixedStatusDialog,
  useVoucherDetailWorkflow,
  useVoucherTabs,
} from '@/features/manual-voucher-approval'
import { CustomButton } from '@/shared/ui/button'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { CustomTab } from '@/shared/ui/tab'
import { CustomTabs } from '@/shared/ui/tabs'

export const Route = createFileRoute(
  '/_auth/applications/manual-voucher-approval/$manualVoucherId'
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { manualVoucherId } = Route.useParams()

  const {
    voucher,
    isDraft,
    isActionable,
    isSubmitting,
    isApproving,
    isRejecting,
    dialogAction,
    dialogComment,
    setDialogComment,
    showMixedStatusDialog,
    handleBack,
    handleSubmitForReview,
    handleApprove,
    handleMixedStatusClose,
    handleMixedStatusConfirm,
    handleDialogOpen,
    handleDialogClose,
    handleDialogConfirm,
    handlePartialApprove,
  } = useVoucherDetailWorkflow(manualVoucherId)

  const { activeTab, handleTabChange } = useVoucherTabs(manualVoucherId)

  return (
    <PageWrapper title={t('Карточка заявки')}>
      <Box display="flex" gap={2} alignItems="center">
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary" key="1">
            {t('Заявки на подтверждение фиксации')}
          </Typography>
          <Typography key="2" color="text.primary">
            {t('Карточка заявки')}
          </Typography>
        </Breadcrumbs>
      </Box>
      <CustomTabs value={activeTab} variant="scrollable" scrollButtons="auto">
        <CustomTab onClick={() => handleTabChange('common')} value="common" label={t('Основное')} />
        <CustomTab
          onClick={() => handleTabChange('items')}
          value="items"
          label={t('Записи ручного учёта посещения столовой и получения питания учащихся')}
        />
        <CustomTab
          onClick={() => handleTabChange('history')}
          value="history"
          label={t('История')}
        />
      </CustomTabs>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Outlet />
      </Box>

      {isDraft && (
        <Box
          sx={{
            borderTop: '1px solid #D0D0D1',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            padding: '24px 0 24px 0',
            marginTop: 'auto',
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <CustomButton
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSubmitForReview}
            loading={isSubmitting}
            disabled={isSubmitting}
            sx={{ flexShrink: 0 }}
          >
            {t('Перевести в статус "На рассмотрении"')}
          </CustomButton>
          <CustomButton
            type="button"
            color="secondary"
            sx={{ width: { xs: '100%', sm: '220px' }, flexShrink: 0 }}
            variant="contained"
            onClick={handleBack}
          >
            {t('Закрыть')}
          </CustomButton>
        </Box>
      )}

      {isActionable && (
        <Box
          sx={{
            borderTop: '1px solid #D0D0D1',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            padding: '24px 0 24px 0',
            marginTop: 'auto',
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <CustomButton
            type="button"
            color="primary"
            variant="contained"
            onClick={handleApprove}
            disabled={isApproving || isRejecting}
            sx={{ flexShrink: 0 }}
            startIcon={<CheckIcon />}
          >
            {t('Одобрить')}
          </CustomButton>
          {(voucher?.items?.length ?? 0) > 1 && (
            <CustomButton
              type="button"
              color="secondary"
              variant="contained"
              onClick={handlePartialApprove}
              sx={{ flexShrink: 0 }}
              startIcon={<CheckIcon />}
            >
              {t('Частично одобрить')}
            </CustomButton>
          )}
          <CustomButton
            type="button"
            color="error"
            variant="contained"
            onClick={() => handleDialogOpen('reject')}
            sx={{ flexShrink: 0, color: '#fff' }}
            startIcon={<CloseIcon />}
          >
            {t('Отклонить')}
          </CustomButton>
        </Box>
      )}

      <ApprovalCommentDialog
        open={!!dialogAction}
        action={dialogAction}
        comment={dialogComment}
        onCommentChange={setDialogComment}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        isLoading={dialogAction === 'approve' ? isApproving : isRejecting}
      />

      <MixedStatusDialog
        open={showMixedStatusDialog}
        onClose={handleMixedStatusClose}
        onConfirm={handleMixedStatusConfirm}
        isLoading={isApproving}
      />
    </PageWrapper>
  )
}
